import { Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createReleaseSync } from './releaseSync.js';

// Release protocol (BestPractice guides 24 and 30): the bundle knows its own version;
// /version.json (static, no-store) tells which version is published. When they differ the
// page reloads by itself and shows what changed after the reload.

export const APP_VERSION = __APP_VERSION__;

export interface ReleaseArea {
  area: string;
  items: string[];
}

export interface VersionDocument {
  schemaVersion: '1.0' | '1.1';
  applicationId: string;
  version: string;
  publishedAt: string;
  commit?: string;
  title: string;
  areas?: ReleaseArea[];
  items?: string[];
}

export interface ReleaseNotesDocument {
  schemaVersion: '1.0' | '1.1';
  applicationId: string;
  releases: Array<{ version: string; publishedAt: string; commit?: string; summary: string; areas?: ReleaseArea[]; changes?: string[] }>;
}

/** `areas` wins when present; the flat 1.0 form (`items` / `changes`) becomes a single unnamed area. */
export function releaseAreas(source: { areas?: ReleaseArea[]; items?: string[]; changes?: string[] }): ReleaseArea[] {
  if (Array.isArray(source.areas) && source.areas.length) return source.areas.filter((group) => Array.isArray(group.items) && group.items.length);
  const flat = source.items ?? source.changes ?? [];
  return flat.length ? [{ area: '', items: flat }] : [];
}

export function ReleaseAreas({ areas }: { areas: ReleaseArea[] }) {
  return (
    <>
      {areas.map((group, index) => (
        <div key={group.area || index} className="release-group">
          {group.area ? <p className="release-area">{group.area}</p> : null}
          <ul>
            {group.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

/**
 * Guida 30, § 4: appena `/version.json` dichiara una versione nuova, tutte le schede si ricaricano
 * da sole, senza conferma. Le note della versione (`broker.release.notes`, gestite da releaseSync)
 * compaiono dopo il ricaricamento finché non si chiudono con «Ho capito», in tutte le schede.
 * Controlli solo su eventi (avvio, focus, visibilità, rete): nessun timer.
 */
export function ReleaseUpdateGuard() {
  const [notes, setNotes] = useState<VersionDocument>();
  const [sync] = useState(() =>
    createReleaseSync({
      appId: 'broker',
      bundleVersion: APP_VERSION,
      onNotesChange: (found) => setNotes(found as unknown as VersionDocument | undefined),
    }),
  );

  useEffect(() => {
    setNotes(sync.notes() as unknown as VersionDocument | undefined);
    return sync.start();
  }, [sync]);

  if (!notes) return null;

  const close = () => {
    sync.dismissNotes();
    setNotes(undefined);
  };

  return createPortal(
    <div className="modal-backdrop release-backdrop">
      <div className="modal release-dialog" role="dialog" aria-modal="true" aria-labelledby="release-title">
        <div className="release-dialog-head">
          <div className="release-icon">
            <Megaphone size={20} />
          </div>
          <span className="eyebrow">AGGIORNATO ALLA VERSIONE {notes.version}</span>
          <h2 id="release-title">{notes.title}</h2>
        </div>
        <div className="release-dialog-body">
          <ReleaseAreas areas={releaseAreas(notes)} />
        </div>
        <div className="release-dialog-foot">
          <button type="button" className="btn btn-primary btn-md full" onClick={close} autoFocus>
            <span>Ho capito</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
